// Shared CRM timeline for a contact (a customer or a prospect): a merged, most-recent
// -first feed of logged interactions plus related quote/project events, with a quick-log
// composer underneath. Used by the prospect detail page and the customer Communications
// tab. All wording is plain English (office-facing) and house-rule clean.
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "../../lib/api";
import type { CommunicationTarget, CommunicationChannel, CommunicationDirection, TimelineItem } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { formatDateTime } from "../../lib/format";

// A stable string key for a target, for the load effect's dependency (the parent may pass a
// fresh target object each render).
function targetKey(t: CommunicationTarget): string {
  return "subcontractorId" in t ? `s:${t.subcontractorId}` : `${t.contactType}:${t.contactId}`;
}

const CHANNELS: { value: CommunicationChannel; label: string }[] = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "meeting", label: "Meeting" },
  { value: "note", label: "Note" },
  { value: "sms", label: "SMS" },
];
const DIRECTIONS: { value: CommunicationDirection; label: string }[] = [
  { value: "outbound", label: "Outbound" },
  { value: "inbound", label: "Inbound" },
  { value: "internal", label: "Internal" },
];

// Look up a display label for a raw enum value, falling back to the raw value so an
// unknown one is never blank. Reuses the composer's label arrays as the source of truth.
const channelLabel = (v: string | null): string => CHANNELS.find((c) => c.value === v)?.label ?? v ?? "";
const directionLabel = (v: string | null): string => DIRECTIONS.find((d) => d.value === v)?.label ?? v ?? "";

const KIND_LABEL: Record<TimelineItem["kind"], string> = {
  communication: "Logged",
  quote: "Quote",
  project: "Project",
  contact: "Milestone",
};
// A CATEGORY colour for the kind of timeline entry, not a status (so it is not in the status registry).
const KIND_INK: Record<TimelineItem["kind"], string> = {
  communication: "text-ele-green",
  quote: "text-ele-blue",
  project: "text-ele-loler",
  contact: "text-ele-body",
};

export function CommunicationTimeline({ target }: { target: CommunicationTarget }) {
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  const [channel, setChannel] = useState<CommunicationChannel>("phone");
  const [direction, setDirection] = useState<CommunicationDirection>("outbound");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Per-item delete (logged interactions only -- quote/project lifecycle events are not
  // deletable here). Confirm-gated; only a "communication" item carries a deletable id.
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.communications.remove(deleteId);
      setDeleteId(null);
      await load();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete that entry. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  const key = targetKey(target);
  const load = useCallback(async () => {
    setState("loading");
    try {
      setItems(await api.communications.timeline(target));
      setState("ready");
    } catch {
      setState("error");
    }
    // `key` captures the target identity; `target` itself is read from the current closure.
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.communications.create({ ...target, channel, direction, subject: subject.trim() || undefined, body: body.trim() });
      setSubject("");
      setBody("");
      await load();
    } catch (err) {
      setFormError(friendlyApiError(err, "Could not log that interaction. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
      {/* Timeline */}
      <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-3 font-heading text-lg text-ele-blue">Timeline</h2>
        {state === "loading" ? (
          <Loader shape="inline" />
        ) : state === "error" ? (
          <p role="alert" className="text-sm text-ele-strong">Could not load the timeline. Please try again in a moment.</p>
        ) : items.length === 0 ? (
          <EmptyState compact message="No interactions logged yet. Use the form to log the first one." />
        ) : (
          <ul className="space-y-3">
            {items.map((it) => (
              <li key={it.id} className="border-l-2 border-ele-purple-accent pl-3">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className={`font-menu text-[10px] uppercase tracking-wide ${KIND_INK[it.kind]}`}>
                    {KIND_LABEL[it.kind]}
                    {it.kind === "communication" && it.channel ? `: ${channelLabel(it.channel)}${it.direction ? ` (${directionLabel(it.direction)})` : ""}` : ""}
                  </span>
                  <span className="text-xs text-ele-body opacity-70">{formatDateTime(it.at)}</span>
                  {it.systemGenerated && <span className="rounded-full border border-ele-purple-accent px-1.5 text-[9px] uppercase text-ele-body">auto</span>}
                  {it.kind === "communication" && it.commId && (
                    <button
                      type="button"
                      onClick={() => { setDeleteError(null); setDeleteId(it.commId); }}
                      className="ml-auto font-menu text-[10px] tracking-wide text-ele-body transition hover:text-ele-danger"
                      aria-label="Delete this logged interaction"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <p className="mt-0.5 text-sm text-ele-strong">{it.title}</p>
                {it.detail && <p className="mt-0.5 whitespace-pre-line text-sm text-ele-body">{it.detail}</p>}
                {it.loggedByName && <p className="mt-0.5 text-xs text-ele-body opacity-60">Logged by {it.loggedByName}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Quick-log composer */}
      <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="mb-3 font-heading text-lg text-ele-blue">Log an interaction</h2>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-ele-body" htmlFor="comm-channel">Channel</label>
              <select
                id="comm-channel"
                value={channel}
                onChange={(e) => setChannel(e.target.value as CommunicationChannel)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green"
              >
                {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-ele-body" htmlFor="comm-direction">Direction</label>
              <select
                id="comm-direction"
                value={direction}
                onChange={(e) => setDirection(e.target.value as CommunicationDirection)}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green"
              >
                {DIRECTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-ele-body" htmlFor="comm-subject">Subject (optional)</label>
            <input
              id="comm-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-ele-body" htmlFor="comm-body">What was said or done</label>
            <textarea
              id="comm-body"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm outline-none focus:border-ele-green"
            />
          </div>
          {formError && <p role="alert" className="text-xs text-ele-strong">{formError}</p>}
          <Button type="submit" disabled={saving || !body.trim()} className="w-full">
            {saving ? "Logging..." : "Log interaction"}
          </Button>
        </form>
      </section>

      <ConfirmDeleteModal
        open={deleteId !== null}
        title="Delete this logged interaction?"
        message="This permanently removes this timeline entry. It cannot be undone."
        confirmLabel="Delete entry"
        singleStep
        busy={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) { setDeleteId(null); setDeleteError(null); } }}
      />
    </div>
  );
}
